/**
 * RevenueRegisterMonthConfirmController — modale de confirmation mensuelle
 * du livre des recettes (EPIC-016 / 16.5, spec §7.H.E, BIC vente uniquement).
 *
 * Pilote la modale `<section id="month-confirmation">` :
 *   - Action `open` : lit `year`/`month` sur le trigger (dataset values),
 *     fallback sur le mois calendaire précédent. Charge `GET
 *     /revenue/months/{year}/{month}/status` pour pré-remplir le
 *     récapitulatif (entrées, total TTC, ventilation par source), puis
 *     révèle la modale.
 *   - Action `close` : ferme la modale.
 *   - Action `confirm` : `POST /revenue/months/{year}/{month}/confirm`.
 *     Au succès, toast + rechargement complet de la page (l'encart
 *     dashboard + le badge mois confirmé + le bandeau d'intégrité hash
 *     sont recalculés côté backend au reload).
 *
 * Au refus :
 *   - 409 `conflict` (déjà confirmé) → toast d'erreur + reload (état
 *     stale côté frontend).
 *   - 422 (BIC vente non éligible, year/month invalide) → toast d'erreur.
 *
 * Zéro HTML inline : les valeurs dynamiques sont injectées via
 * `target.textContent` sur des hooks placés verbatim dans la maquette.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const MONTH_NAMES_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

const ERROR_MESSAGES = {
  not_bic_vente_eligible:
    "Cette action est réservée aux organisations avec une activité BIC vente active.",
  already_confirmed: "Ce mois est déjà confirmé.",
  year_invalid:
    "Année invalide : la confirmation n'est possible qu'entre l'année de création de votre micro-entreprise et l'année courante.",
  month_invalid: "Mois invalide : la valeur doit être comprise entre 1 et 12.",
  validation_failed: "Certains champs sont invalides.",
  business_rule_violation: "Cette action n'est pas autorisée.",
  conflict: "Cette action est en conflit avec l'état actuel.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class RevenueRegisterMonthConfirmController extends BaseController {
  static targets = [
    "title",
    "intro",
    "entriesCount",
    "totalAmountInclVat",
    "franchiseAmount",
    "invoicePaymentsAmount",
    "manualEntriesAmount",
    "submitButton",
    "errorBox",
    "errorMessage",
  ];

  _year = null;
  _month = null;
  _submitting = false;

  async open(event) {
    const trigger = event?.currentTarget ?? null;
    const { year, month } = this._readYearMonth(trigger);
    if (!year || !month) {
      this.showError("Impossible de déterminer le mois à confirmer.");
      return;
    }
    this._year = year;
    this._month = month;

    this._clearError();
    this._writeTitleAndIntro(year, month);

    try {
      const status = await api.getRevenueMonthStatus(year, month);
      if (status?.confirmed === true) {
        this.showError("Ce mois est déjà confirmé.");
        this._reload();
        return;
      }
      this._writeSummary(status ?? {});
    } catch (error) {
      this._handleApiError(error);
      return;
    }

    this._showModal();
  }

  close() {
    this._hideModal();
    this._clearError();
    this._submitting = false;
  }

  async confirm(event) {
    if (event) event.preventDefault();
    if (this._submitting || !this._year || !this._month) return;

    this._submitting = true;
    this._setSubmitButtonState(true);
    this._clearError();

    try {
      await api.confirmRevenueMonth(this._year, this._month);
      this.showSuccess("Mois confirmé.");
      this._reload();
    } catch (error) {
      this._handleApiError(error);
      this._submitting = false;
      this._setSubmitButtonState(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readYearMonth(trigger) {
    const ds = trigger?.dataset ?? {};
    const year = this._toInt(
      ds.revenueRegisterMonthConfirmYearValue ?? ds.year ?? null,
    );
    const month = this._toInt(
      ds.revenueRegisterMonthConfirmMonthValue ?? ds.month ?? null,
    );
    if (year && month) return { year, month };

    // Fallback : mois calendaire précédent à aujourd'hui.
    const today = new Date();
    const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const prev = new Date(firstOfThisMonth.getTime());
    prev.setDate(prev.getDate() - 1);
    return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
  }

  _toInt(value) {
    if (value === null || value === undefined || value === "") return null;
    const n = Number.parseInt(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  _writeTitleAndIntro(year, month) {
    const label = `${MONTH_NAMES_FR[month - 1] ?? ""} ${year}`.trim();
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Confirmer le mois de ${label}`;
    }
    if (this.hasIntroTarget) {
      this.introTarget.textContent =
        `En confirmant, vous attestez avoir saisi toutes les recettes du mois de ${label}. ` +
        "Le récapitulatif et le hash du mois sont scellés à cet instant.";
    }
  }

  _writeSummary(status) {
    if (this.hasEntriesCountTarget) {
      this.entriesCountTarget.textContent = String(status.entriesCount ?? 0);
    }
    const total = status.totalAmountInclVat ?? "0";
    if (this.hasTotalAmountInclVatTarget) {
      this.totalAmountInclVatTarget.textContent = this._formatEur(total);
    }
    // V1 franchise en base : tout le CA est ventilé à 0 %.
    if (this.hasFranchiseAmountTarget) {
      this.franchiseAmountTarget.textContent = this._formatEur(total);
    }
    if (this.hasInvoicePaymentsAmountTarget) {
      this.invoicePaymentsAmountTarget.textContent = this._formatEur(
        status.invoicePaymentsAmount ?? "0",
      );
    }
    if (this.hasManualEntriesAmountTarget) {
      this.manualEntriesAmountTarget.textContent = this._formatEur(
        status.manualEntriesAmount ?? "0",
      );
    }
  }

  _formatEur(value) {
    const num = Number.parseFloat(String(value).replace(",", "."));
    if (!Number.isFinite(num)) return "0,00 €";
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num) + " €";
  }

  _showModal() {
    this.element.hidden = false;
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.focus();
    }
  }

  _hideModal() {
    this.element.hidden = true;
  }

  _setSubmitButtonState(loading) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = loading;
    if (loading) {
      this.submitButtonTarget.dataset.originalLabel =
        this.submitButtonTarget.textContent;
      this.submitButtonTarget.textContent = "Scellement…";
    } else if (this.submitButtonTarget.dataset.originalLabel) {
      this.submitButtonTarget.textContent =
        this.submitButtonTarget.dataset.originalLabel;
      delete this.submitButtonTarget.dataset.originalLabel;
    }
  }

  _handleApiError(error) {
    if (error instanceof APIError) {
      const data = error.data ?? {};
      const code = data?.error?.code ?? "";
      const serverMessage = data?.error?.message ?? "";
      const mapped = ERROR_MESSAGES[code] ?? serverMessage ?? error.message;
      this._showError(mapped || "Erreur lors de la confirmation.");
      return;
    }
    this._showError("Erreur réseau. Veuillez réessayer.");
  }

  _showError(message) {
    if (this.hasErrorBoxTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorBoxTarget.hidden = false;
      this.errorBoxTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    this.showError(message);
  }

  _clearError() {
    if (this.hasErrorBoxTarget) {
      this.errorBoxTarget.hidden = true;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "—";
    }
  }

  _reload() {
    window.location.reload();
  }
}
